'use client';

import { useState, useEffect, useRef } from 'react';
import { apiService } from '@/lib/api';
import { useDate } from '@/contexts/DateContext';
import { Sparkles } from 'lucide-react';

// Helper: convert ASCII number to Devanagari numerals
const toDevanagariNumeral = (num: number): string => {
  const devanagariDigits = ['०','१','२','३','४','५','६','७','८','९'];
  return String(num).split('').map(d => devanagariDigits[Number(d)]).join('');
};

export default function EventsSidebar({ embedded = false }: { embedded?: boolean } = {}) {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { dateData, loading: contextLoading } = useDate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  // Utility to scroll to element with highlight
  const scrollToElement = (target: HTMLElement) => {
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('ring-2', 'ring-accent-400/40', 'bg-accent-50');
      setTimeout(() => {
        try { target.classList.remove('ring-2', 'ring-accent-400/40', 'bg-accent-50'); } catch (err) {}
      }, 3500);
    } catch (e) {}
  };

  // Enriched event cache used for previewing/sorting (moved up for helpers)
  const [enrichedEvents, setEnrichedEvents] = useState<any[]>([]);

  // Navigation controls removed: Prev/Next/Today not required
  // (previously: getList, navIndex, findNearestIndex, scrollToIndex, scrollToPrev/Next/Today)



  useEffect(() => {
    const onSelect = (e: any) => {
      const sel = e?.detail?.nepaliDate;
      if (!sel || !containerRef.current) return;
      try {
        const target = containerRef.current.querySelector(`[data-nepali-date="${sel}"]`);
        if (target && target instanceof HTMLElement) {
          // Smooth scroll and highlight
          scrollToElement(target as HTMLElement);
        }
      } catch (err) {
        // No-op
      }
    };

    const onDateSelected = (e: CustomEvent) => {
      const { nepali } = e.detail;
      if (nepali) {
        // Load events for the selected date
        loadEventsForDate(nepali);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:selectDate', onSelect as EventListener);
      window.addEventListener('calendar:dateSelected', onDateSelected as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:selectDate', onSelect as EventListener);
        window.removeEventListener('calendar:dateSelected', onDateSelected as EventListener);
      }
    };
  }, []);


  const convCacheRef = useRef<Record<string, any>>({});

  const loadEventsForDate = async (dateObj: any) => {
    try {
      setLoading(true);
      if (dateObj) {
        const { year, month } = dateObj;
        const response: any = await apiService.getEvents(year, month);
        if (response?.success && response?.data) {
          const rawEvents = Array.isArray(response.data) ? response.data : [];
          setEvents(rawEvents);
          // Enrich events with english date and counters
          enrichEvents(rawEvents, year);
        } else {
          setEvents([]);
          setEnrichedEvents([]);
        }
      } else {
        setEvents([]);
        setEnrichedEvents([]);
      }
    } catch (error) {
      // Silently fail - don't show error to user
      setEvents([]);
      setEnrichedEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      if (dateData?.nepali) {
        const { year, month } = dateData.nepali;
        const response: any = await apiService.getEvents(year, month);
        if (response?.success && response?.data) {
          const rawEvents = Array.isArray(response.data) ? response.data : [];
          setEvents(rawEvents);
          // Enrich events with english date and counters
          enrichEvents(rawEvents, year);
        } else {
          setEvents([]);
          setEnrichedEvents([]);
        }
      } else {
        setEvents([]);
        setEnrichedEvents([]);
      }
    } catch (error) {
      // Silently fail - don't show error to user
      setEvents([]);
      setEnrichedEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper: compute diff of days between two ISO dates (YYYY-MM-DD) in UTC
  const diffDaysUTC = (aIso: string, bIso: string) => {
    try {
      const [ay, am, ad] = aIso.split('-').map(Number);
      const [by, bm, bd] = bIso.split('-').map(Number);
      const a = Date.UTC(ay, (am || 1) - 1, ad || 1);
      const b = Date.UTC(by, (bm || 1) - 1, bd || 1);
      const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
      return diff;
    } catch (e) { return NaN; }
  };

  // Enrich events with english date and counters relative to current Nepali year (Baisakh-based counting)
  const enrichEvents = async (rawEvents: any[], currentNepaliYear: number) => {
    const results: any[] = [];
    for (const ev of rawEvents) {
      let nepaliDateStr = '';
      if (ev.nepaliDate) {
        nepaliDateStr = typeof ev.nepaliDate === 'string' ? ev.nepaliDate : `${ev.nepaliDate.year}-${String(ev.nepaliDate.month).padStart(2,'0')}-${String(ev.nepaliDate.day).padStart(2,'0')}`;
      } else if (ev.nepali_date) {
        nepaliDateStr = typeof ev.nepali_date === 'string' ? ev.nepali_date : '';
      }

      // Build occurrence for this Nepali year (use currentNepaliYear)
      const parts = nepaliDateStr.split('-');
      let monthPart = parts[1] || '';
      let dayPart = parts[2] || '';
      // Ensure zero-padded
      monthPart = String(Number(monthPart) || 0).padStart(2, '0');
      dayPart = String(Number(dayPart) || 0).padStart(2, '0');

      const thisYearNepali = `${currentNepaliYear}-${monthPart}-${dayPart}`;
      const nextYearNepali = `${currentNepaliYear + 1}-${monthPart}-${dayPart}`;

      // Convert (use cache)
      const getConversion = async (nepStr: string) => {
        if (!nepStr) return null;
        if (convCacheRef.current[nepStr]) return convCacheRef.current[nepStr];
        try {
          const resp: any = await apiService.convertDate('nepali', 'english', nepStr);
          if (resp?.success && resp?.data) {
            convCacheRef.current[nepStr] = resp.data;
            return resp.data;
          }
        } catch (e) { /* ignore */ }
        return null;
      };

      const convThis = await getConversion(thisYearNepali);
      const convNext = await getConversion(nextYearNepali);

      // Fallback english date from event if provided
      const englishFromEvent = ev.englishDate || ev.english_date || (ev.english && ev.english.date) || null;

      const thisIso =
        convThis?.english?.date ||
        (convThis?.english ? `${convThis.english.year}-${String(convThis.english.month).padStart(2,'0')}-${String(convThis.english.day).padStart(2,'0')}` : null) ||
        englishFromEvent ||
        (nepaliDateStr ? nepaliDateStr : '');
      const nextIso =
        convNext?.english?.date ||
        (convNext?.english ? `${convNext.english.year}-${String(convNext.english.month).padStart(2,'0')}-${String(convNext.english.day).padStart(2,'0')}` : null) ||
        null;

      // Today's english ISO from DateContext (build from year/month/day if `date` field is absent)
      const todayIso = dateData?.english
        ? `${dateData.english.year}-${String(dateData.english.month).padStart(2,'0')}-${String(dateData.english.day).padStart(2,'0')}`
        : new Date().toISOString().split('T')[0];

      let daysAgo = 0;
      let daysUntil = NaN;
      let englishFormatted =
        convThis?.english?.formatted ||
        (thisIso ? new Date(thisIso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : (englishFromEvent ? new Date(englishFromEvent).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''));

      if (thisIso) {
        // diff positive => event is in future, negative => event is in past
        const diff = diffDaysUTC(todayIso, thisIso);
        if (!isNaN(diff)) {
          if (diff > 0) {
            // occurs in future
            daysUntil = diff;
            daysAgo = 0;
          } else if (diff < 0) {
            // Already occurred in current selected Nepali month/year.
            // Keep it in "Past" only; do not push it to upcoming via next-year recurrence.
            daysAgo = Math.abs(diff);
            daysUntil = NaN;
          } else {
            daysAgo = 0;
            daysUntil = 0;
          }
        }
      }

      results.push({
        ...ev,
        nepaliDateStr,
        eventNepaliYear: Number(parts[0]) || null,
        eventNepaliMonth: Number(parts[1]) || null,
        englishFormatted,
        daysAgo,
        daysUntil,
      });
    }

    setEnrichedEvents(results);
  };

  useEffect(() => {
    if (dateData) {
      loadEvents();
      // Refresh events every 5 minutes
      const interval = setInterval(loadEvents, 5 * 60 * 1000);
      return () => clearInterval(interval);
    } else if (!contextLoading) {
      setLoading(false);
    }
  }, [dateData, contextLoading]);

  // Filter events into upcoming and past
  const currentNepaliYear = Number(dateData?.nepali?.year) || null;
  const currentNepaliMonth = Number(dateData?.nepali?.month) || null;

  const upcomingEvents = enrichedEvents.filter((event) => {
    if (!(Number.isFinite(event.daysUntil) && event.daysUntil >= 0)) return false;
    if (!currentNepaliYear || !currentNepaliMonth) return false;
    return event.eventNepaliYear === currentNepaliYear && event.eventNepaliMonth === currentNepaliMonth;
  }).sort((a, b) => a.daysUntil - b.daysUntil);

  const pastEvents = enrichedEvents.filter(event => 
    event.daysAgo > 0
  ).sort((a, b) => b.daysAgo - a.daysAgo);

  const displayEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  return (
    <div className={embedded ? 'w-full' : 'bg-white dark:bg-dark-800 rounded-xl shadow-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 w-full'}>
      {!embedded && (
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-accent-500" />
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Festivals & Events
          </h3>
        </div>
      )}

      {/* Tab Navigation */}
      {enrichedEvents.length > 0 && (
        <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'upcoming'
                ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Upcoming ({upcomingEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'past'
                ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Past ({pastEvents.length})
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="spinner mx-auto"></div>
        </div>
      ) : displayEvents.length > 0 ? (
        <div ref={containerRef} id="events-sidebar" tabIndex={-1} className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {displayEvents.map((event, index) => {
            // Normalize nepali date (backend may return "nepaliDate" as string or object, or snake_case "nepali_date")
            let nepaliDateStr = '';
            let dateDisplay = '';
            const nepaliMonths = [
              'बैशाख', 'जेष्ठ', 'आषाढ', 'श्रावण', 'भाद्र', 'आश्विन',
              'कार्तिक', 'मंसिर', 'पौष', 'माघ', 'फाल्गुन', 'चैत्र'
            ];

            try {
              if (event.nepaliDate) {
                if (typeof event.nepaliDate === 'string') {
                  nepaliDateStr = event.nepaliDate;
                  const parts = nepaliDateStr.split('-');
                  if (parts.length >= 3) {
                    const day = Number(parts[2]);
                    const month = Number(parts[1]);
                    const monthName = nepaliMonths[month - 1] || '';
                    dateDisplay = `${toDevanagariNumeral(day)} ${monthName}`;
                  } else {
                    dateDisplay = nepaliDateStr;
                  }
                } else if (typeof event.nepaliDate === 'object') {
                  const { year, month, day } = event.nepaliDate;
                  nepaliDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  dateDisplay = `${toDevanagariNumeral(Number(day))} ${nepaliMonths[Number(month) - 1] || ''}`;
                }
              } else if (event.nepali_date) {
                if (typeof event.nepali_date === 'string') {
                  nepaliDateStr = event.nepali_date;
                  const parts = nepaliDateStr.split('-');
                  if (parts.length >= 3) {
                    const day = Number(parts[2]);
                    const month = Number(parts[1]);
                    dateDisplay = `${toDevanagariNumeral(day)} ${nepaliMonths[month - 1] || ''}`;
                  } else {
                    dateDisplay = nepaliDateStr;
                  }
                }
              }
            } catch (e) {
              // fallback: leave dateDisplay empty
            }

            const elementId = nepaliDateStr ? `event-${nepaliDateStr}-${index}` : `event-unknown-${index}`;

            return (
              <div
                key={index}
                id={elementId}
                data-nepali-date={nepaliDateStr}
                className={`p-3 sm:p-4 rounded-lg transition-colors ${
                  event.isHoliday || event.type === 'holiday'
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : event.type === 'festival'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                    : 'bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-gray-700'
                }`}
              >
                {(dateDisplay || event.englishFormatted) && (
                  <div className="mb-2 flex items-center gap-2 text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white">
                    {dateDisplay && (
                      <span className="devanagari whitespace-nowrap">{dateDisplay}</span>
                    )}
                    {event.englishFormatted && (
                      <span className="text-[12px] sm:text-sm font-medium text-gray-500 dark:text-gray-300 whitespace-nowrap">
                        {event.englishFormatted}
                      </span>
                    )}
                  </div>
                )}

                <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-1">
                  {typeof (event.title || event.event_title) === 'string' 
                    ? (event.title || event.event_title || 'Untitled Event')
                    : String(event.title || event.event_title || 'Untitled Event')}
                </div>

                {/* Counter: occurred / upcoming - based on active tab */}
                {activeTab === 'upcoming' && Number.isFinite(event.daysUntil) && event.daysUntil >= 0 && (
                  <div className="text-xs sm:text-sm font-semibold text-accent-600 dark:text-accent-400 mb-2">
                    {event.daysUntil === 0 ? 'Today!' : `In ${event.daysUntil} day${event.daysUntil !== 1 ? 's' : ''}`}
                  </div>
                )}

                {activeTab === 'past' && event.daysAgo > 0 && (
                  <div className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                    ⏳ Occurred {event.daysAgo} day{event.daysAgo !== 1 ? 's' : ''} ago
                  </div>
                )}

                {event.description && (
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {typeof event.description === 'string' ? event.description : String(event.description || '')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-600 dark:text-gray-400">
          <p className="text-xs sm:text-sm">
            {activeTab === 'upcoming' ? 'No upcoming events' : 'No past events'}
          </p>
        </div>
      )}
    </div>
  );
}

